import { useState, useEffect, useMemo, useContext, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  TextField,
  Stack,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
  Checkbox,
  Switch,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import LanOutlinedIcon from "@mui/icons-material/LanOutlined";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import ExpandCircleDownOutlinedIcon from "@mui/icons-material/ExpandCircleDownOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import LanguageIcon from "@mui/icons-material/Language";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import GitHubIcon from "@mui/icons-material/GitHub";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import DeployDialog from "../components/DeployDialog";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import DashboardLayout from "../layouts/DashboardLayout";
import { HEADING_FONT } from "../theme/fonts";
import { COLORS } from "../theme/tokens";
import { AuthContext } from "../context/AuthContext";
import FlatCard from "../components/FlatCard";
import DomainDetailDrawer from "../components/DomainDetailDrawer";
import AddDomainModal from "../components/AddDomainModal";
import AddSubdomainModal from "../components/AddSubdomainModal";
import ImportExportModal from "../components/ImportExportModal";
import AppSelect from "../components/AppSelect";
import TableSkeleton from "../components/TableSkeleton";
import { useToast } from "../components/ToastProvider";
import { useConfirm } from "../components/ConfirmProvider";
import api from "../api";
import {
  useDomains,
  useCategories,
  useDeleteDomain,
  useBulkDeleteDomains,
  useDeleteAllDomains,
  useDeleteSubdomain,
} from "../hooks/queries";

// Trust the stored status (Active / Near Expiry / Expired / New) if present;
// otherwise fall back to computing from the expiry date.
function getExpiryStatus(domain) {
  if (domain.status) return domain.status;
  if (!domain.expiry) return "Active";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(domain.expiry) >= today ? "Active" : "Expired";
}

// A site the last scan found not working shows "Down" — except an expired
// domain, where being down is expected and "Expired" is the real story.
function getStatus(domain) {
  const status = getExpiryStatus(domain);
  if (domain.websiteIsUp === false && status !== "Expired") return "Down";
  return status;
}

function getDaysLeft(expiryDate) {
  if (!expiryDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiryDate);
  return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
}

const STATUS_STYLES = {
  Active: {
    color: "#16A34A",
    bg: "rgba(22, 163, 74, 0.12)",
    icon: "check",
  },
  "Near Expiry": {
    color: "#D97706",
    bg: "rgba(217, 119, 6, 0.12)",
    icon: "warning",
  },
  Expired: {
    color: "#DC2626",
    bg: "rgba(220, 38, 38, 0.12)",
    icon: "error",
  },
  Down: {
    color: "#DC2626",
    bg: "rgba(220, 38, 38, 0.12)",
    icon: "error",
  },
  New: {
    color: "#3B82F6",
    bg: "rgba(59, 130, 246, 0.12)",
    icon: "check",
  },
};

function getStatusStyle(status) {
  return STATUS_STYLES[status] || STATUS_STYLES.Active;
}

// Sort orders offered in the toolbar. `value` is sent to the API as `sort` and
// must match a key in the backend's SORT_MAP (routes/domains.js) — anything it
// doesn't recognise falls back to "newest".
const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "nameAsc", label: "Name (A–Z)" },
  { value: "nameDesc", label: "Name (Z–A)" },
  { value: "expirySoon", label: "Expiry (soonest)" },
  { value: "expiryLate", label: "Expiry (latest)" },
];

// Toolbar dropdowns (category + sort). MUI reserves 32px of right padding for
// the arrow, which leaves a wide dead gap next to a short label — tightened
// here so the two selects stay narrow and the search field keeps the rest.
const compactSelectSx = {
  flexShrink: 0,
  // Hug the selected label instead of sitting at a fixed width — a fixed box is
  // what left the dead space between the text and the arrow.
  width: "auto",
  maxWidth: 190,
  "& .MuiSelect-select": {
    paddingRight: "24px !important",
    pl: 1.25,
    minWidth: "0 !important",
  },
  "& .MuiSvgIcon-root": { right: 1 },
};

// Turn the /domains response into { rows, total, counts } for the current page.
// The backend normally returns { rows, total, counts }; if an older build
// returns a bare array, we paginate + tally on the client so the page still
// works during a partial deploy.
const EMPTY_COUNTS = { all: 0, active: 0, nearExpiry: 0, expired: 0, new: 0, inactive: 0, down: 0 };

function normalizeDomains(data, page, pageSize) {
  if (!data) return { rows: [], total: 0, counts: EMPTY_COUNTS };
  if (Array.isArray(data)) {
    const tally = { all: data.length, active: 0, nearExpiry: 0, expired: 0, new: 0, inactive: 0, down: 0 };
    data.forEach((d) => {
      const s = getStatus(d);
      if (s === "Active") tally.active++;
      else if (s === "Near Expiry") tally.nearExpiry++;
      else if (s === "Expired") tally.expired++;
      else if (s === "New") tally.new++;
      if (d.websiteIsUp === false) tally.down++;
    });
    const start = (page - 1) * pageSize;
    return { rows: data.slice(start, start + pageSize), total: data.length, counts: tally };
  }
  return {
    rows: data.rows || [],
    total: data.total || 0,
    counts: data.counts || EMPTY_COUNTS,
  };
}

function DomainList() {
  // Flat spec design tokens (light-only). The page used to read these from the
  // glass theme (useThemeColors); mapping the same keys onto the spec palette
  // keeps every existing `colors.*` reference working while locking the page to
  // the flat "Blue · Light" design system (no dark-mode dependency).
  const colors = {
    text: COLORS.ink,
    textMuted: COLORS.muted,
    textFaint: COLORS.faint,
    cardBg: COLORS.surface,
    menuBg: COLORS.surface,
    cardBorder: COLORS.hairline,
    cardBorderHover: COLORS.borderHover,
    hover: COLORS.tile,
    divider: COLORS.hairline,
  };
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const { hasPermission } = useContext(AuthContext);
  const canAdd = hasPermission("domains", "add");
  const canEdit = hasPermission("domains", "edit");
  const canDelete = hasPermission("domains", "delete");

  // Add Domain popup modal
  const [addOpen, setAddOpen] = useState(false);
  // Right-side detail drawer (spec "View") — holds the domain row being viewed.
  const [viewDomain, setViewDomain] = useState(null);
  // Domain whose "Deploy" dialog is open (ZIP → its server over SSH).
  const [deployDomain, setDeployDomain] = useState(null);
  const [viewSubdomain, setViewSubdomain] = useState(null);
  // Edit Domain wizard modal — holds the id of the domain being edited.
  const [editDomainId, setEditDomainId] = useState(null);

  async function handleDomainUpdated() {
    setEditDomainId(null);
    await queryClient.invalidateQueries({ queryKey: ["domains"] });
    // A domain write can create/enrich the linked client (+ its location).
    queryClient.invalidateQueries({ queryKey: ["clients"] });
    queryClient.invalidateQueries({ queryKey: ["locations"] });
    queryClient.invalidateQueries({ queryKey: ["sublocations"] });
    toast.success("Domain updated successfully");
  }
  // Add / Edit Sub-Domain popup modal
  const [addSubOpen, setAddSubOpen] = useState(false);
  const [editSub, setEditSub] = useState(null);
  const deleteSubdomain = useDeleteSubdomain();

  async function handleDomainAdded(created) {
    setAddOpen(false);
    await queryClient.invalidateQueries({ queryKey: ["domains"] });
    // Adding a domain can also create the linked client + its location.
    queryClient.invalidateQueries({ queryKey: ["clients"] });
    queryClient.invalidateQueries({ queryKey: ["locations"] });
    queryClient.invalidateQueries({ queryKey: ["sublocations"] });
    if (created?.ftpError) {
      // The domain is saved; only its FTP details were not.
      toast.warning(`Domain added, but its FTP details were not saved: ${created.ftpError}`);
    } else if (created?.zipError) {
      // Domain + repo are fine; only pushing the ZIP's contents failed.
      toast.warning(`Domain added, but the ZIP upload failed: ${created.zipError}`);
    } else if (created?.zipUploaded) {
      toast.success("Domain added, repo created and ZIP uploaded");
    } else if (created?.githubRepoStatus === "created") {
      toast.success("Domain added and GitHub repo created");
    } else if (created?.githubRepoStatus === "failed") {
      // The domain itself is saved — only the repo step failed (retryable).
      toast.warning(
        `Domain added, but the GitHub repo failed${created.githubRepoError ? `: ${created.githubRepoError}` : ""}. Retry from the domain's page.`
      );
    } else {
      toast.success("Domain added successfully");
    }
  }

  function handleSubdomainAdded() {
    setAddSubOpen(false);
    queryClient.invalidateQueries({ queryKey: ["subdomains"] });
    queryClient.invalidateQueries({ queryKey: ["domains"] });
    toast.success("Sub-domain added successfully");
  }

  function handleSubdomainSaved() {
    setEditSub(null);
    queryClient.invalidateQueries({ queryKey: ["subdomains"] });
    queryClient.invalidateQueries({ queryKey: ["domains"] });
    toast.success("Sub-domain updated");
  }

  async function handleDeleteSubdomain(sub) {
    const ok = await confirm({
      title: "Delete sub-domain?",
      message: `"${sub.name}" will be permanently removed.`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteSubdomain.mutateAsync(sub.id);
      await queryClient.invalidateQueries({ queryKey: ["domains"] });
      toast.info("Sub-domain deleted");
    } catch (err) {
      toast.error(err.message || "Failed to delete sub-domain");
    }
  }

  // Manual active/inactive toggle per domain.
  const [togglingId, setTogglingId] = useState(null);
  async function handleToggleActive(domain) {
    setTogglingId(domain.id);
    try {
      await api.patch(`/domains/${domain.id}/active`, { active: !domain.isActive });
      await queryClient.invalidateQueries({ queryKey: ["domains"] });
      toast.success(
        domain.isActive
          ? `${domain.name} set to Inactive`
          : `${domain.name} set to Active`
      );
    } catch (err) {
      toast.error(err.message || "Failed to update domain");
    } finally {
      setTogglingId(null);
    }
  }

  // Which domain rows have their sub-domains expanded (accordion). Collapsed by
  // default so a domain with many sub-domains doesn't fill the page.
  const [expandedDomains, setExpandedDomains] = useState(() => new Set());
  // While a search is running every matching row opens by default (so a matched
  // sub-domain is never hidden). This holds the rows the user has explicitly
  // CLOSED during that search — without it the arrow was a no-op mid-search,
  // because the "search is active" check always forced the row back open.
  const [collapsedDomains, setCollapsedDomains] = useState(() => new Set());

  const toggleSet = (setter, id) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  function isExpanded(id) {
    if (debouncedSearch) return !collapsedDomains.has(id);
    return expandedDomains.has(id);
  }

  function toggleExpanded(id) {
    if (debouncedSearch) toggleSet(setCollapsedDomains, id);
    else toggleSet(setExpandedDomains, id);
  }

  const [searchParams, setSearchParams] = useSearchParams();

  // Open the Add Domain modal automatically when arriving via the dashboard
  // quick-action (/domains?add=1), then strip the param so a refresh/back
  // doesn't re-open it.
  useEffect(() => {
    if (searchParams.get("add") === "1") {
      setAddOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("add");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const VALID_FILTERS = ["all", "active", "down", "nearExpiry", "expired", "new", "inactive"];
  const initialFilter = (() => {
    const f = searchParams.get("filter");
    return f && VALID_FILTERS.includes(f) ? f : "all";
  })();

  const [search, setSearch] = useState("");
  // Debounced copy of `search` so we hit the API after the user stops typing,
  // not on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const initialCategory = (() => {
    const categoryFromUrl = searchParams.get("category");
    return categoryFromUrl || "all";
  })();
  const [categoryFilter, setCategoryFilter] = useState(initialCategory);
  const [filter, setFilter] = useState(initialFilter);
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Import / Export modal
  const [showImportExport, setShowImportExport] = useState(false);

  // Build the query params from the active page + filters. React Query
  // re-fetches automatically whenever this object changes — and serves the
  // cache when we revisit the same combination.
  const params = useMemo(() => {
    const p = { limit: pageSize, offset: (page - 1) * pageSize };
    if (debouncedSearch.trim()) p.search = debouncedSearch.trim();
    if (filter !== "all") p.status = filter;
    if (categoryFilter !== "all") p.category = categoryFilter;
    p.sort = sort;
    return p;
  }, [page, pageSize, debouncedSearch, filter, categoryFilter, sort]);

  // placeholderData keeps the previous page on screen while the next one loads,
  // so paging/filtering never flashes an empty table.
  const domainsQuery = useDomains(params, { placeholderData: (prev) => prev });
  const { rows, total, counts } = useMemo(
    () => normalizeDomains(domainsQuery.data, page, pageSize),
    [domainsQuery.data, page, pageSize]
  );
  const loading = domainsQuery.isFetching;

  // Surface a load failure as a toast (matches the old behaviour).
  useEffect(() => {
    if (domainsQuery.isError) {
      toast.error(domainsQuery.error?.message || "Failed to load domains");
    }
  }, [domainsQuery.isError, domainsQuery.error, toast]);

  // Debounce the search box → debouncedSearch.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  // All saved categories for the filter dropdown (full list, cached).
  const categoriesQuery = useCategories();
  const categoryOptions = useMemo(
    () => (categoriesQuery.data || []).map((c) => c.name).filter(Boolean),
    [categoriesQuery.data]
  );

  // Create/update/delete mutations — each invalidates the domains cache so
  // every view refreshes without a manual reload call.
  const deleteDomain = useDeleteDomain();
  const bulkDeleteDomains = useBulkDeleteDomains();
  const deleteAllDomains = useDeleteAllDomains();

  // Row selection (checkbox column). Holds the ids checked on the current page.
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  async function handleDelete(id) {
    try {
      await deleteDomain.mutateAsync(id);
      toast.info("Domain deleted");
    } catch (err) {
      toast.error(err.message || "Failed to delete domain");
    }
  }

  // --- Row selection + bulk delete ---
  function toggleRow(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  async function handleDeleteSelected() {
    const ids = [...selectedIds];
    if (!ids.length) return;
    const ok = await confirm({
      title: `Delete ${ids.length} domain${ids.length === 1 ? "" : "s"}?`,
      message: "The selected domains will be permanently removed.",
      danger: true,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    try {
      const res = await bulkDeleteDomains.mutateAsync(ids);
      clearSelection();
      toast.success(`Deleted ${res?.deleted ?? ids.length} domain(s)`);
    } catch (err) {
      toast.error(err.message || "Failed to delete selected domains");
    }
  }

  async function handleDeleteAll() {
    const ok = await confirm({
      title: "Delete ALL domains?",
      message:
        "Every domain in the table will be permanently removed. This cannot be undone.",
      danger: true,
      confirmLabel: "Delete all",
    });
    if (!ok) return;
    try {
      const res = await deleteAllDomains.mutateAsync();
      clearSelection();
      toast.success(`Deleted ${res?.deleted ?? 0} domain(s)`);
    } catch (err) {
      toast.error(err.message || "Failed to delete all domains");
    }
  }

  // Category dropdown options: all saved categories, plus any category seen on
  // the rows currently loaded (so nothing on the page is hidden).
  const categories = useMemo(() => {
    const set = new Set(categoryOptions);
    rows.forEach((d) => {
      if (d.category) set.add(d.category);
      (d.subdomains || []).forEach((sub) => {
        if (sub.categoryName) set.add(sub.categoryName);
      });
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [rows, categoryOptions]);

  useEffect(() => {
    if (categoryFilter === "all") return;
    const targetCategory = categoryFilter.trim().toLowerCase();
    setExpandedDomains((prev) => {
      const next = new Set(prev);
      rows.forEach((domain) => {
        const domainMatches = (domain.category || "").trim().toLowerCase() === targetCategory;
        const subMatches = (domain.subdomains || []).some((sub) => {
          const subCategory = (sub.categoryName || "").trim().toLowerCase();
          return subCategory === targetCategory;
        });
        if (domainMatches || subMatches) next.add(domain.id);
        else if (!debouncedSearch && prev.has(domain.id)) next.delete(domain.id);
      });
      return next;
    });
  }, [categoryFilter, rows, debouncedSearch]);

  useEffect(() => {
    const categoryFromUrl = searchParams.get("category") || "all";
    setCategoryFilter((prev) => (prev !== categoryFromUrl ? categoryFromUrl : prev));
  }, [searchParams]);

  function applyCategoryFilter(nextValue) {
    setCategoryFilter(nextValue);
    const next = new URLSearchParams(searchParams);
    if (nextValue && nextValue !== "all") next.set("category", nextValue);
    else next.delete("category");
    setSearchParams(next, { replace: true });
  }

  function changePage(nextPage) {
    setPage(nextPage);
    const next = new URLSearchParams(searchParams);
    next.set("page", String(nextPage));
    setSearchParams(next, { replace: true });
  }

  // Reset to page 1 when the (debounced) search, filter, sort, or page size changes.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filter, categoryFilter, sort, pageSize]);

  // A row closed during one search shouldn't stay closed for the next one.
  useEffect(() => {
    setCollapsedDomains(new Set());
  }, [debouncedSearch]);

  // Drop any row selection when the visible set changes (page / search / filter)
  // so the checkboxes always reflect what's actually on screen.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, debouncedSearch, filter, categoryFilter, sort, pageSize]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);

  // If the current page falls past the end (e.g. after deleting the last row on
  // the last page), step back so we never show an empty page.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const startIndex = (safePage - 1) * pageSize;
  const rangeStart = total === 0 ? 0 : startIndex + 1;
  const rangeEnd = Math.min(startIndex + pageSize, total);

  // Compact page-number list with ellipses (e.g. 1 … 4 5 6 … 12)
  function buildPageList(current, total) {
    const pages = new Set([1, total, current, current - 1, current + 1]);
    const sorted = [...pages]
      .filter((n) => n >= 1 && n <= total)
      .sort((a, b) => a - b);
    const out = [];
    let prev = 0;
    for (const n of sorted) {
      if (n - prev > 1) out.push("...");
      out.push(n);
      prev = n;
    }
    return out;
  }
  const pageList = buildPageList(safePage, totalPages);

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      background: colors.cardBg,
      color: colors.text,
      borderRadius: "10px",
      "& fieldset": { borderColor: colors.cardBorder },
      "&:hover fieldset": { borderColor: colors.cardBorderHover },
      "&.Mui-focused fieldset": { borderColor: "#3B82F6" },
    },
    "& input": { color: colors.text },
  };

  // Leading 44px track is the checkbox column. The minimums add up to fit a
  // 1366px-wide laptop next to the sidebar; the last track holds up to five
  // 32px action buttons.
  const GRID_COLUMNS =
    "44px minmax(170px, 1.7fr) minmax(110px, 1fr) 110px 110px minmax(115px, 1fr) minmax(120px, 1.1fr) 70px 176px";
  // Header and rows are separate grids: none may shrink below the columns'
  // total (tracks + gaps + padding), or the last column spills past the
  // header's background and the row dividers. Narrower screens scroll instead.
  const GRID_MIN_WIDTH = { xs: "max-content", md: 1201 };

  // Header checkbox state for "select all on this page".
  const allOnPageSelected =
    rows.length > 0 && rows.every((d) => selectedIds.has(d.id));
  const someOnPageSelected =
    rows.some((d) => selectedIds.has(d.id)) && !allOnPageSelected;

  function toggleAllOnPage() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        rows.forEach((d) => next.delete(d.id));
      } else {
        rows.forEach((d) => next.add(d.id));
      }
      return next;
    });
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 4, justifyContent: "space-between" }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontFamily: HEADING_FONT,
              fontWeight: 700,
              color: colors.text,
              letterSpacing: "-0.5px",
            }}
          >
            Domains
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: colors.textMuted, mt: 0.5 }}
          >
            {counts.all}{" "}
            {counts.all === 1 ? "domain" : "domains"} in your portfolio
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{
            alignSelf: { xs: "flex-end", sm: "center" },
            ml: { sm: "auto" },
            justifyContent: { xs: "flex-end" },
          }}
        >
          {canDelete && (
            <Button
              onClick={handleDeleteAll}
              startIcon={<DeleteOutlineIcon />}
              disabled={deleteAllDomains.isPending}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                px: 2,
                py: 1.1,
                borderRadius: "10px",
                color: "#DC2626",
                background: "rgba(239, 68, 68, 0.1)",
                border: "1px solid rgba(239, 68, 68, 0.25)",
                "&:hover": { background: "rgba(239, 68, 68, 0.18)" },
                transition: "all 0.15s ease",
              }}
            >
              Delete All
            </Button>
          )}

          <Button
            onClick={() => setShowImportExport(true)}
            startIcon={<ImportExportIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
              px: 2,
              py: 1.1,
              borderRadius: "10px",
              color: colors.text,
              background: colors.cardBg,
              border: `1px solid ${colors.cardBorder}`,
              "&:hover": {
                background: colors.hover,
                borderColor: colors.cardBorderHover,
              },
              transition: "all 0.15s ease",
            }}
          >
            Import / Export
          </Button>

          {canAdd && (
            <Button
              onClick={() => setAddSubOpen(true)}
              startIcon={<LanOutlinedIcon sx={{ fontSize: 18 }} />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                px: 2.5,
                py: 1.1,
                borderRadius: "10px",
                color: "#3B82F6",
                background: colors.cardBg,
                border: "1px solid rgba(59, 130, 246, 0.45)",
                "&:hover": {
                  background: "rgba(59, 130, 246, 0.10)",
                  borderColor: "rgba(59, 130, 246, 0.7)",
                  transform: "translateY(-1px)",
                  boxShadow: "0 6px 16px rgba(59, 130, 246, 0.22)",
                },
                transition: "all 0.15s ease",
              }}
            >
              Add Sub-Domain
            </Button>
          )}

          {canAdd && (
            <Button
              onClick={() => setAddOpen(true)}
              startIcon={<AddIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                px: 2.5,
                py: 1.1,
                borderRadius: "10px",
                color: "#fff",
                background:
                  "#3B82F6",
                boxShadow: "0 4px 14px rgba(59, 130, 246, 0.35)",
                "&:hover": {
                  boxShadow: "0 8px 22px rgba(59, 130, 246, 0.45)",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.15s ease",
              }}
            >
              Add Domain
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Import / Export modal */}
      <ImportExportModal
        open={showImportExport}
        onClose={() => setShowImportExport(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["domains"] });
          queryClient.invalidateQueries({ queryKey: ["subdomains"] });
          toast.success("Import/Export completed successfully");
        }}
      />


      {/* Filter + Search Bar */}
      <FlatCard
        sx={{
          p: 2,
          mb: 3,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          alignItems: { md: "center" },
        }}
      >
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_, value) => value && setFilter(value)}
          sx={{
            // Mobile: keep the tabs on one line and scroll sideways so none
            // (Expired/New) get clipped. Desktop (md+) unchanged.
            maxWidth: "100%",
            flexWrap: { xs: "nowrap", md: "wrap" },
            overflowX: { xs: "auto", md: "visible" },
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontSize: 13,
              fontWeight: 600,
              px: 2,
              py: 0.75,
              flexShrink: 0,
              whiteSpace: "nowrap",
              border: `1px solid ${colors.cardBorder}`,
              color: colors.textMuted,
              "&:hover": { background: colors.hover },
              "&.Mui-selected": {
                background: "rgba(59, 130, 246, 0.12)",
                color: "#3B82F6",
                borderColor: "#3B82F6",
                "&:hover": { background: "rgba(59, 130, 246, 0.18)" },
              },
            },
          }}
        >
          <ToggleButton value="all">
            All
            <Chip
              size="small"
              label={counts.all}
              sx={{
                ml: 1,
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                background: colors.hover,
                color: colors.textMuted,
              }}
            />
          </ToggleButton>
          <ToggleButton value="active">
            Active
            <Chip
              size="small"
              label={counts.active}
              sx={{
                ml: 1,
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                background: "rgba(16, 185, 129, 0.15)",
                color: "#16A34A",
              }}
            />
          </ToggleButton>
          <ToggleButton value="down">
            Down
            <Chip
              size="small"
              label={counts.down}
              sx={{
                ml: 1,
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                background: "rgba(220, 38, 38, 0.15)",
                color: "#DC2626",
              }}
            />
          </ToggleButton>
          <ToggleButton value="nearExpiry">
            Expiring
            <Chip
              size="small"
              label={counts.nearExpiry}
              sx={{
                ml: 1,
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                background: "rgba(245, 158, 11, 0.15)",
                color: "#D97706",
              }}
            />
          </ToggleButton>
          <ToggleButton value="expired">
            Expired
            <Chip
              size="small"
              label={counts.expired}
              sx={{
                ml: 1,
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                background: "rgba(239, 68, 68, 0.15)",
                color: "#DC2626",
              }}
            />
          </ToggleButton>
          <ToggleButton value="inactive">
            Inactive
            <Chip
              size="small"
              label={counts.inactive}
              sx={{
                ml: 1,
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                background: "rgba(148, 163, 184, 0.18)",
                color: "#94a3b8",
              }}
            />
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Category filter selector */}
        <AppSelect
          value={categoryFilter}
          onChange={(e) => applyCategoryFilter(e.target.value)}
          sx={compactSelectSx}
        >
          <MenuItem value="all" sx={{ fontSize: 13, color: colors.text }}>
            All Categories
          </MenuItem>
          {categories.map((c) => (
            <MenuItem key={c} value={c} sx={{ fontSize: 13, color: colors.text }}>
              {c}
            </MenuItem>
          ))}
        </AppSelect>

        {/* Sort order — applied server-side, so it orders the whole list and
            not just the rows on the current page. */}
        <AppSelect
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          sx={compactSelectSx}
        >
          {SORT_OPTIONS.map((o) => (
            <MenuItem
              key={o.value}
              value={o.value}
              sx={{ fontSize: 13, color: colors.text }}
            >
              {o.label}
            </MenuItem>
          ))}
        </AppSelect>

        <TextField
          size="small"
          placeholder="Search across all fields..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            ...fieldSx,
            flex: 1,
            "& .MuiOutlinedInput-root": {
              ...fieldSx["& .MuiOutlinedInput-root"],
              fontSize: 13,
            },
          }}
          InputProps={{
            startAdornment: (
              <SearchIcon
                sx={{ color: colors.textFaint, mr: 1, fontSize: 18 }}
              />
            ),
          }}
        />
      </FlatCard>

      {/* Selection action bar — shows when one or more rows are checked */}
      {selectedIds.size > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 2,
            px: 2.5,
            py: 1.5,
            borderRadius: "10px",
            background: "rgba(239, 68, 68, 0.08)",
            border: "1px solid rgba(239, 68, 68, 0.25)",
          }}
        >
          <Typography
            sx={{ fontSize: 14, fontWeight: 600, color: colors.text, flex: 1 }}
          >
            {selectedIds.size} selected
          </Typography>
          {canDelete && (
            <Button
              onClick={handleDeleteSelected}
              startIcon={<DeleteOutlineIcon />}
              disabled={bulkDeleteDomains.isPending}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13,
                px: 2,
                py: 0.75,
                borderRadius: "10px",
                color: "#fff",
                background: "linear-gradient(135deg, #DC2626 0%, #dc2626 100%)",
                boxShadow: "0 4px 14px rgba(239, 68, 68, 0.35)",
                "&:hover": { transform: "translateY(-1px)" },
                transition: "all 0.15s ease",
              }}
            >
              Delete Selected
            </Button>
          )}
          <Button
            onClick={clearSelection}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              color: colors.textMuted,
              "&:hover": { background: colors.hover },
            }}
          >
            Clear
          </Button>
        </Box>
      )}

      {/* Empty state */}
      {rows.length === 0 && loading ? (
        <FlatCard sx={{ overflow: "hidden" }}>
          <TableSkeleton rows={pageSize} columns={7} />
        </FlatCard>
      ) : rows.length === 0 ? (
        <FlatCard
          sx={{
            py: 8,
            textAlign: "center",
          }}
        >
          <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  mx: "auto",
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#3B82F6",
                  background: "rgba(59, 130, 246, 0.12)",
                  border: "1px solid rgba(59, 130, 246, 0.25)",
                }}
              >
                <LanguageIcon sx={{ fontSize: 36 }} />
              </Box>
              <Typography
                sx={{
                  fontFamily: HEADING_FONT,
                  fontSize: 16,
                  fontWeight: 700,
                  color: colors.text,
                  mb: 0.5,
                }}
              >
                {counts.all === 0 ? "No domains yet" : "No matches found"}
              </Typography>
              <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
                {counts.all === 0
                  ? 'Click "Add Domain" to register your first one'
                  : "Try a different search or filter"}
              </Typography>
        </FlatCard>
      ) : (
        /* Domain table */
        <FlatCard
          sx={{
            overflow: "hidden",
          }}
        >
          {/* Horizontal scroll wrapper — keeps the full table on small screens
              (swipe sideways) instead of squishing the columns. */}
          <Box sx={{ overflowX: "auto" }}>
          {/* Table header */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: GRID_COLUMNS,
              minWidth: GRID_MIN_WIDTH,
              gap: 2,
              px: 3,
              py: 1.75,
              borderBottom: `1px solid ${colors.cardBorder}`,
              background: colors.hover,
              fontFamily: HEADING_FONT,
              fontSize: 12,
              fontWeight: 600,
              color: colors.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Checkbox
                size="small"
                checked={allOnPageSelected}
                indeterminate={someOnPageSelected}
                onChange={toggleAllOnPage}
                sx={{ p: 0, color: colors.textMuted }}
              />
            </Box>
            <Box sx={{ fontFamily: HEADING_FONT }}>Domain</Box>
            <Box sx={{ fontFamily: HEADING_FONT }}>Category</Box>
            <Box sx={{ fontFamily: HEADING_FONT }}>Status</Box>
            <Box sx={{ fontFamily: HEADING_FONT }}>Website</Box>
            <Box sx={{ fontFamily: HEADING_FONT }}>IP Address</Box>
            <Box sx={{ fontFamily: HEADING_FONT }}>Expiry</Box>
            <Box sx={{ fontFamily: HEADING_FONT, textAlign: "center" }}>Active</Box>
            <Box sx={{ fontFamily: HEADING_FONT, textAlign: "center" }}>Actions</Box>
          </Box>

          {/* Rows */}
          {rows.map((domain, idx) => {
            const status = getStatus(domain);
            const days = getDaysLeft(domain.expiry);
            const style = getStatusStyle(status);
            const statusColor = style.color;
            const statusBg = style.bg;
            const websiteChecked = typeof domain.websiteIsUp === "boolean";
            const websiteLabel = !websiteChecked
              ? "Not checked"
              : domain.websiteIsUp
                ? "Up"
                : "Down";
            const websiteColor = !websiteChecked
              ? colors.textMuted
              : domain.websiteIsUp
                ? "#16A34A"
                : "#DC2626";
            const websiteBg = !websiteChecked
              ? "rgba(100, 116, 139, 0.12)"
              : domain.websiteIsUp
                ? "rgba(22, 163, 74, 0.12)"
                : "rgba(220, 38, 38, 0.12)";

            return (
              <Fragment key={domain.id}>
              <Box
                sx={{
                  position: "relative",
                  display: "grid",
                  gridTemplateColumns: GRID_COLUMNS,
                  minWidth: GRID_MIN_WIDTH,
                  gap: 2,
                  px: 3,
                  py: 2,
                  alignItems: "center",
                  borderBottom: `1px solid ${colors.divider}`,
                  "&:last-child": { borderBottom: "none" },
                  // Status-coloured accent bar on the left edge, revealed on hover.
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: "3px",
                    background: style.color,
                    opacity: 0,
                    transition: "opacity 0.2s ease",
                  },
                  "&:hover": {
                    background: "#F8FAFC",
                  },
                  "&:hover::before": { opacity: 1 },
                  transition: "background 0.18s ease",
                }}
              >
                {/* Row select checkbox */}
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    size="small"
                    checked={selectedIds.has(domain.id)}
                    onChange={() => toggleRow(domain.id)}
                    sx={{ p: 0, color: colors.textMuted }}
                  />
                </Box>

                {/* Domain name + registrar */}
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ minWidth: 0 }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "10px",
                      background: statusBg,
                      color: statusColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <LanguageIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Box
                      component="a"
                      href={
                        domain.name
                          ? domain.name.startsWith("http")
                            ? domain.name
                            : `https://${domain.name}`
                          : "#"
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        fontFamily: HEADING_FONT,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        textDecoration: "none",
                        fontSize: 14,
                        fontWeight: 600,
                        color: colors.text,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "100%",
                        cursor: "pointer",
                        transition: "color 0.15s ease",
                        "&:hover": {
                          color: "#3B82F6",
                          "& .external-icon": { opacity: 1 },
                        },
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {domain.name || "(unnamed)"}
                      </Box>
                      <OpenInNewIcon
                        className="external-icon"
                        sx={{
                          fontSize: 12,
                          opacity: 0,
                          transition: "opacity 0.15s ease",
                          flexShrink: 0,
                        }}
                      />
                      {/* At-a-glance marker: this domain has a GitHub repo.
                          Clicking it opens the repo instead of the website. */}
                      {domain.githubRepoUrl && (
                        <Tooltip title="Open the GitHub repo">
                          <GitHubIcon
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              window.open(domain.githubRepoUrl, "_blank", "noopener");
                            }}
                            sx={{
                              fontSize: 14,
                              flexShrink: 0,
                              color: colors.textMuted,
                              "&:hover": { color: colors.text },
                            }}
                          />
                        </Tooltip>
                      )}
                      {domain.githubRepoStatus === "failed" && (
                        <Tooltip title="GitHub repo failed — open the domain to retry">
                          <GitHubIcon sx={{ fontSize: 14, flexShrink: 0, color: "#f59e0b" }} />
                        </Tooltip>
                      )}
                    </Box>
                    {domain.clientCode && (
                      <Typography
                        sx={{
                          fontFamily: HEADING_FONT,
                          fontSize: 12,
                          color: colors.textMuted,
                          fontWeight: 500,
                          mt: 0.25,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Client · {domain.clientCode}
                      </Typography>
                    )}
                  </Box>
                </Stack>

                {/* Category */}
                <Box sx={{ minWidth: 0 }}>
                  {(domain.category || (categoryFilter !== "all" ? categoryFilter : "")) ? (
                    <Chip
                      size="small"
                      label={domain.category || categoryFilter}
                      sx={{
                        fontFamily: HEADING_FONT,
                        height: 22,
                        fontWeight: 600,
                        fontSize: 11,
                        color: "#3B82F6",
                        background: "rgba(59, 130, 246, 0.12)",
                        border: "1px solid rgba(59, 130, 246, 0.25)",
                        maxWidth: "100%",
                        "& .MuiChip-label": {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        },
                      }}
                    />
                  ) : (
                    <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.textFaint }}>
                      —
                    </Typography>
                  )}
                </Box>

                {/* Status — dot badge (spec) */}
                <Box>
                  {(() => {
                    const isInactive = domain.isActive === false;
                    const dotColor = isInactive ? "#94a3b8" : statusColor;
                    const dotBg = isInactive ? "rgba(148, 163, 184, 0.15)" : statusBg;
                    const dotLabel = isInactive ? "Inactive" : status;
                    return (
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.75,
                          px: 1.25,
                          py: 0.4,
                          borderRadius: 999,
                          fontFamily: HEADING_FONT,
                          fontWeight: 700,
                          fontSize: 11,
                          color: dotColor,
                          background: dotBg,
                        }}
                      >
                        <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: dotColor, flexShrink: 0 }} />
                        {dotLabel}
                      </Box>
                    );
                  })()}
                </Box>

                {/* Live availability from the most recent uptime check */}
                <Box>
                  <Tooltip
                    title={
                      domain.websiteLastChecked
                        ? `Last checked: ${new Date(domain.websiteLastChecked).toLocaleString()}`
                        : "No health check yet"
                    }
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                        px: 1.25,
                        py: 0.4,
                        borderRadius: 999,
                        fontFamily: HEADING_FONT,
                        fontWeight: 700,
                        fontSize: 11,
                        color: websiteColor,
                        background: websiteBg,
                      }}
                    >
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          background: websiteColor,
                          flexShrink: 0,
                        }}
                      />
                      {websiteLabel}
                    </Box>
                  </Tooltip>
                </Box>

                {/* IP address — a domain can have several; show the first and
                    count the rest so the row height stays fixed. */}
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
                  <Typography
                    noWrap
                    sx={{
                      fontSize: 13,
                      color: colors.text,
                      fontFamily:
                        "ui-monospace, SFMono-Regular, Menlo, monospace",
                    }}
                  >
                    {domain.ip || "—"}
                  </Typography>
                  {domain.ipAddresses?.length > 1 && (
                    <Tooltip title={domain.ipAddresses.join(", ")}>
                      <Box
                        component="span"
                        sx={{
                          flexShrink: 0,
                          px: 0.6,
                          py: 0.1,
                          borderRadius: "6px",
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#3B82F6",
                          background: "rgba(59, 130, 246, 0.12)",
                        }}
                      >
                        +{domain.ipAddresses.length - 1}
                      </Box>
                    </Tooltip>
                  )}
                </Stack>

                {/* Expiry */}
                <Stack direction="column" spacing={0.25}>
                  <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.text }}>
                    {domain.expiry || "—"}
                  </Typography>
                  {days !== null && (
                    <Typography
                      sx={{
                        fontFamily: HEADING_FONT,
                        fontSize: 11,
                        fontWeight: 700,
                        color: statusColor,
                      }}
                    >
                      {days < 0
                        ? `Expired ${Math.abs(days)}d ago`
                        : `${days}d left`}
                    </Typography>
                  )}
                </Stack>

                {/* Active / Inactive toggle */}
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Tooltip
                    title={
                      domain.isActive !== false
                        ? "Active — click to deactivate"
                        : "Inactive — click to activate"
                    }
                  >
                    <span>
                      <Switch
                        size="small"
                        checked={domain.isActive !== false}
                        onChange={() => handleToggleActive(domain)}
                        disabled={!canEdit || togglingId === domain.id}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": { color: "#16A34A" },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "#16A34A",
                          },
                        }}
                      />
                    </span>
                  </Tooltip>
                </Box>

                {/* Actions — icon only */}
                <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                  <Tooltip title="View">
                    <IconButton
                      size="small"
                      onClick={() => setViewDomain(domain)}
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1.5,
                        color: "#0ea5e9",
                        background: "rgba(14, 165, 233, 0.1)",
                        border: "1px solid rgba(14, 165, 233, 0.25)",
                        "&:hover": { background: "rgba(14, 165, 233, 0.18)" },
                      }}
                    >
                      <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                  {canEdit && (
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => setEditDomainId(domain.id)}
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1.5,
                          color: "#3B82F6",
                          background: "rgba(59, 130, 246, 0.1)",
                          border: "1px solid rgba(59, 130, 246, 0.25)",
                          "&:hover": { background: "rgba(59, 130, 246, 0.18)" },
                        }}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={async () => {
                          if (
                            await confirm({
                              title: "Delete domain?",
                              message: "This action cannot be undone.",
                              danger: true,
                              confirmLabel: "Delete",
                            })
                          )
                            handleDelete(domain.id);
                        }}
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1.5,
                          color: "#DC2626",
                          background: "rgba(239, 68, 68, 0.1)",
                          border: "1px solid rgba(239, 68, 68, 0.25)",
                          "&:hover": { background: "rgba(239, 68, 68, 0.18)" },
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canEdit && (
                    <Tooltip title="Deploy a ZIP to this domain's server">
                      <IconButton
                        size="small"
                        onClick={() => setDeployDomain(domain)}
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1.5,
                          color: "#7C3AED",
                          background: "rgba(124, 58, 237, 0.1)",
                          border: "1px solid rgba(124, 58, 237, 0.25)",
                          "&:hover": { background: "rgba(124, 58, 237, 0.18)" },
                        }}
                      >
                        <RocketLaunchOutlinedIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {domain.subdomains?.length > 0 && (
                    <Tooltip
                      title={
                        isExpanded(domain.id)
                          ? "Hide sub-domains"
                          : `Show ${domain.subdomains.length} sub-domain${
                              domain.subdomains.length > 1 ? "s" : ""
                            }`
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={() => toggleExpanded(domain.id)}
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1.5,
                          color: "#3B82F6",
                          background: "rgba(59, 130, 246, 0.1)",
                          border: "1px solid rgba(59, 130, 246, 0.25)",
                          "&:hover": { background: "rgba(59, 130, 246, 0.18)" },
                        }}
                      >
                        <ExpandCircleDownOutlinedIcon
                          sx={{
                            fontSize: 18,
                            transition: "transform 0.2s ease",
                            transform: isExpanded(domain.id)
                              ? "rotate(180deg)"
                              : "none",
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Box>

              {/* Nested sub-domain rows — shown when the parent is expanded, or
                  always while searching (so a matched sub-domain stays visible) */}
              {isExpanded(domain.id) &&
                domain.subdomains?.map((sub) => {
                const subStatusColor =
                  sub.status === "Active"
                    ? "#16A34A"
                    : sub.status === "Down"
                    ? "#DC2626"
                    : colors.textMuted;
                const subSslColor =
                  sub.sslStatus === "valid"
                    ? "#16A34A"
                    : sub.sslStatus === "invalid"
                    ? "#DC2626"
                    : colors.textMuted;
                return (
                  <Box
                    key={sub.id}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: GRID_COLUMNS,
                      minWidth: GRID_MIN_WIDTH,
                      gap: 2,
                      px: 3,
                      py: 1.5,
                      alignItems: "center",
                      borderBottom: `1px solid ${colors.divider}`,
                      "&:last-child": { borderBottom: "none" },
                      // Subtle purple tint + left accent bar to read as a child
                      // of the domain row directly above it.
                      background:
                        "linear-gradient(90deg, rgba(59,130,246,0.08) 0%, rgba(59,130,246,0.03) 100%)",
                      boxShadow: "inset 3px 0 0 0 rgba(59,130,246,0.55)",
                      "&:hover": { background: "rgba(59,130,246,0.12)" },
                      transition: "background 0.15s ease",
                    }}
                  >
                    {/* connector arrow — sits in the checkbox column, pulled left */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      <SubdirectoryArrowRightIcon
                        sx={{ fontSize: 20, color: "#3B82F6", ml: -1 }}
                      />
                    </Box>
                    {/* Sub-domain name — aligned directly under the parent name */}
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{ minWidth: 0 }}
                    >
                      <Box
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: "10px",
                          background:
                            "linear-gradient(135deg, rgba(59,130,246,0.28) 0%, rgba(59,130,246,0.28) 100%)",
                          color: "#3B82F6",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <LanguageIcon sx={{ fontSize: 18 }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Box
                          component="button"
                          type="button"
                          onClick={() => setViewSubdomain(sub)}
                          sx={{
                            fontFamily: HEADING_FONT,
                            display: "block",
                            textDecoration: "none",
                            fontSize: 13,
                            fontWeight: 600,
                            color: colors.text,
                            maxWidth: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            background: "transparent",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                            textAlign: "left",
                            "&:hover": { color: "#3B82F6" },
                          }}
                        >
                          {sub.name}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: 11,
                            color: colors.textFaint,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          part of {domain.name}
                        </Typography>
                      </Box>
                    </Stack>
                    {/* Category col */}
                    <Box>
                      {(sub.categoryName || (categoryFilter !== "all" ? categoryFilter : "")) ? (
                        <Chip
                          size="small"
                          label={sub.categoryName || categoryFilter}
                          sx={{
                            height: 20,
                            fontWeight: 700,
                            fontSize: 10,
                            color: "#3B82F6",
                            background: "rgba(59, 130, 246, 0.12)",
                            border: "1px solid rgba(59, 130, 246, 0.25)",
                            maxWidth: 120,
                            "& .MuiChip-label": {
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            },
                          }}
                        />
                      ) : (
                        <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 12, color: colors.textFaint }}>
                          —
                        </Typography>
                      )}
                    </Box>
                    {/* Status — dot badge (spec) */}
                    <Box>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.75,
                          px: 1.25,
                          py: 0.4,
                          borderRadius: 999,
                          fontFamily: HEADING_FONT,
                          fontWeight: 700,
                          fontSize: 11,
                          color: subStatusColor,
                          background: `${subStatusColor}1f`,
                        }}
                      >
                        <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: subStatusColor, flexShrink: 0 }} />
                        {sub.status || "Unknown"}
                      </Box>
                    </Box>
                    {/* Subdomain live status is already shown in its Status cell. */}
                    <Box />
                    {/* IP */}
                    <Typography
                      sx={{
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        fontSize: 12.5,
                        color: sub.ip ? colors.text : colors.textFaint,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {sub.ip || "—"}
                    </Typography>
                    {/* Expiry col → SSL */}
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: subSslColor,
                      }}
                    >
                      {sub.sslValidity != null ? `SSL ${sub.sslValidity}d` : "SSL —"}
                      {sub.sslStatus ? ` · ${sub.sslStatus}` : ""}
                    </Typography>
                    {/* Active column — not applicable to sub-domains */}
                    <Box />
                    {/* Actions → View parent · Edit · Delete */}
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title="View details">
                        <IconButton
                          size="small"
                          onClick={() => setViewSubdomain(sub)}
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1.5,
                            color: "#0ea5e9",
                            background: "rgba(14, 165, 233, 0.1)",
                            border: "1px solid rgba(14, 165, 233, 0.25)",
                            "&:hover": { background: "rgba(14, 165, 233, 0.18)" },
                          }}
                        >
                          <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                      {canEdit && (
                        <Tooltip title="Edit sub-domain">
                          <IconButton
                            size="small"
                            onClick={() => setEditSub(sub)}
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 1.5,
                              color: "#3B82F6",
                              background: "rgba(59, 130, 246, 0.1)",
                              border: "1px solid rgba(59, 130, 246, 0.25)",
                              "&:hover": { background: "rgba(59, 130, 246, 0.18)" },
                            }}
                          >
                            <EditOutlinedIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canDelete && (
                        <Tooltip title="Delete sub-domain">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteSubdomain(sub)}
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 1.5,
                              color: "#DC2626",
                              background: "rgba(239, 68, 68, 0.1)",
                              border: "1px solid rgba(239, 68, 68, 0.25)",
                              "&:hover": { background: "rgba(239, 68, 68, 0.18)" },
                            }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>
                );
              })}
              </Fragment>
            );
          })}
          </Box>

          {/* Pagination footer */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "center" }}
            spacing={2}
            sx={{
              width: "100%",
              justifyContent: "space-between",
              px: 3,
              py: 2,
              borderTop: `1px solid ${colors.cardBorder}`,
              background: colors.hover,
            }}
          >
            {/* Left: range */}
            <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
              Showing{" "}
              <Box component="span" sx={{ fontWeight: 700, color: colors.text }}>
                {rangeStart}–{rangeEnd}
              </Box>{" "}
              of{" "}
              <Box component="span" sx={{ fontWeight: 700, color: colors.text }}>
                {total}
              </Box>
            </Typography>

            {/* Right: page size (left of arrows) + page nav, pinned far right */}
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
            >
              <AppSelect
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                MenuProps={{
                  anchorOrigin: { vertical: "top", horizontal: "left" },
                  transformOrigin: { vertical: "bottom", horizontal: "left" },
                }}
                menuSx={{
                  mb: 0.5,
                  minWidth: 72,
                  "& .MuiMenuItem-root": {
                    minHeight: 32,
                    justifyContent: "center",
                  },
                }}
                sx={{
                  mr: 0.5,
                  height: 32,
                  minWidth: 62,
                  fontWeight: 700,
                  transition: "border-color 0.15s ease, background 0.15s ease",
                  "& .MuiSelect-select": {
                    py: 0,
                    pl: 1.5,
                    pr: "28px !important",
                    display: "flex",
                    alignItems: "center",
                  },
                  "&:hover": { background: colors.hover },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.cardBorderHover,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderWidth: "1px",
                  },
                  "& .MuiSvgIcon-root": { right: 4 },
                }}
              >
                {[10, 25, 50, 100].map((n) => (
                  <MenuItem key={n} value={n} sx={{ fontSize: 13 }}>
                    {n}
                  </MenuItem>
                ))}
              </AppSelect>

              <Tooltip title="First page">
                <span>
                  <IconButton
                    size="small"
                    disabled={safePage === 1}
                    onClick={() => changePage(1)}
                    sx={{
                      color: colors.textMuted,
                      "&:hover": { background: colors.cardBg, color: "#3B82F6" },
                    }}
                  >
                    <FirstPageIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Previous">
                <span>
                  <IconButton
                    size="small"
                    disabled={safePage === 1}
                    onClick={() => changePage(Math.max(1, safePage - 1))}
                    sx={{
                      color: colors.textMuted,
                      "&:hover": { background: colors.cardBg, color: "#3B82F6" },
                    }}
                  >
                    <ChevronLeftIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>

              {pageList.map((p, i) =>
                p === "..." ? (
                  <Box
                    key={`ellipsis-${i}`}
                    sx={{
                      px: 1,
                      fontSize: 13,
                      color: colors.textFaint,
                    }}
                  >
                    …
                  </Box>
                ) : (
                  <Box
                    key={p}
                    component="button"
                    onClick={() => changePage(p)}
                    sx={{
                      minWidth: 32,
                      height: 32,
                      px: 1,
                      borderRadius: 1.5,
                      border: "1px solid",
                      borderColor:
                        p === safePage ? "#3B82F6" : "transparent",
                      background:
                        p === safePage
                          ? "rgba(59, 130, 246, 0.12)"
                          : "transparent",
                      color: p === safePage ? "#3B82F6" : colors.text,
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                      "&:hover": {
                        background:
                          p === safePage
                            ? "rgba(59, 130, 246, 0.18)"
                            : colors.cardBg,
                      },
                    }}
                  >
                    {p}
                  </Box>
                )
              )}

              <Tooltip title="Next">
                <span>
                  <IconButton
                    size="small"
                    disabled={safePage === totalPages}
                    onClick={() => changePage(Math.min(totalPages, safePage + 1))}
                    sx={{
                      color: colors.textMuted,
                      "&:hover": { background: colors.cardBg, color: "#3B82F6" },
                    }}
                  >
                    <ChevronRightIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Last page">
                <span>
                  <IconButton
                    size="small"
                    disabled={safePage === totalPages}
                    onClick={() => changePage(totalPages)}
                    sx={{
                      color: colors.textMuted,
                      "&:hover": { background: colors.cardBg, color: "#3B82F6" },
                    }}
                  >
                    <LastPageIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </FlatCard>
      )}

      <AddDomainModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSuccess={handleDomainAdded}
      />

      {/* Edit Domain — same 4-step wizard in edit mode. key remounts the form
          per domain so the prefill effect re-runs. */}
      <AddDomainModal
        key={editDomainId || "edit"}
        open={Boolean(editDomainId)}
        editId={editDomainId}
        onClose={() => setEditDomainId(null)}
        onSuccess={handleDomainUpdated}
      />

      <AddSubdomainModal
        open={addSubOpen}
        onClose={() => setAddSubOpen(false)}
        onSuccess={handleSubdomainAdded}
      />

      {editSub && (
        <AddSubdomainModal
          key={editSub.id}
          open
          editId={editSub.id}
          initialName={editSub.name}
          initialIp={editSub.ip || ""}
          initialPublicIp={editSub.publicIp || editSub.ip || ""}
          initialPrivateIp={editSub.privateIp || ""}
          initialParentId={editSub.parentDomainId ?? null}
          initialServerId={editSub.serverId ?? null}
          initialCategoryId={editSub.categoryId ?? null}
          initialSslValidity={editSub.sslValidity ?? null}
          initialSslStatus={editSub.sslStatus ?? ""}
          initialStatus={editSub.status ?? ""}
          onClose={() => setEditSub(null)}
          onSuccess={handleSubdomainSaved}
        />
      )}

      <DeployDialog
        domain={deployDomain}
        open={Boolean(deployDomain)}
        onClose={() => setDeployDomain(null)}
      />

      {/* Right-side detail drawer (spec "View") */}
      <DomainDetailDrawer
        domain={viewDomain}
        open={Boolean(viewDomain)}
        onClose={() => setViewDomain(null)}
        canEdit={canEdit}
        onEdit={(d) => {
          setViewDomain(null);
          setEditDomainId(d.id);
        }}
      />

      <Dialog
        open={Boolean(viewSubdomain)}
        onClose={() => setViewSubdomain(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            border: `1px solid ${colors.divider}`,
            boxShadow: "0 24px 64px rgba(15, 23, 42, 0.16)",
          },
        }}
      >
        {viewSubdomain && (
          <>
            <DialogTitle
              sx={{
                px: 3,
                py: 2.5,
                color: "#fff",
                background: "linear-gradient(135deg, #1D4ED8 0%, #3B82F6 100%)",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.75}>
                <Box sx={{ width: 46, height: 46, borderRadius: "12px", background: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <LanOutlinedIcon sx={{ fontSize: 25 }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "1.4px", textTransform: "uppercase", opacity: 0.85 }}>
                    Sub-Domain
                  </Typography>
                  <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 21, fontWeight: 700, letterSpacing: "-0.4px", lineHeight: 1.15, wordBreak: "break-word" }}>
                    {viewSubdomain.name || "(unnamed)"}
                  </Typography>
                </Box>
                <IconButton onClick={() => setViewSubdomain(null)} sx={{ color: "rgba(255,255,255,0.9)", flexShrink: 0, "&:hover": { background: "rgba(255,255,255,0.15)", color: "#fff" } }}>
                  <CloseIcon />
                </IconButton>
              </Stack>
            </DialogTitle>
            <DialogContent sx={{ px: 3, pt: 0, pb: 3, background: "#fcfdff" }}>
              {/* Field tiles — 2-column grid (matches the Server view modal).
                  Header gap via margin-top (DialogContent's pt is zeroed by MUI
                  when it directly follows DialogTitle). */}
              <Box sx={{ mt: 3, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                {[
                  { label: "Public IP", value: viewSubdomain.publicIp || viewSubdomain.ip || "—", mono: true },
                  { label: "Private IP", value: viewSubdomain.privateIp || "—", mono: true },
                  {
                    label: "SSL",
                    value:
                      (viewSubdomain.sslValidity != null ? `SSL ${viewSubdomain.sslValidity}d` : "SSL —") +
                      (viewSubdomain.sslStatus ? ` · ${viewSubdomain.sslStatus}` : ""),
                  },
                  { label: "Server", value: viewSubdomain.serverName || "—" },
                ].map(({ label, value, mono }) => (
                  <Box key={label} sx={{ p: 1.75, borderRadius: "10px", border: `1px solid ${colors.cardBorder || colors.divider}`, background: colors.hover }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", color: colors.textMuted, mb: 0.5 }}>
                      {label}
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: colors.text, fontFamily: mono ? "ui-monospace, SFMono-Regular, Menlo, monospace" : undefined, wordBreak: "break-all" }}>
                      {value}
                    </Typography>
                  </Box>
                ))}

                {viewSubdomain.name && (
                  <Box sx={{ gridColumn: "1 / -1", p: 1.75, borderRadius: "10px", border: `1px solid ${colors.cardBorder || colors.divider}`, background: colors.hover }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", color: colors.textMuted, mb: 0.5 }}>
                      Link
                    </Typography>
                    <Box
                      component="a"
                      href={viewSubdomain.name.startsWith("http") ? viewSubdomain.name : `https://${viewSubdomain.name}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ fontSize: 13, fontWeight: 600, color: "#3B82F6", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 0.5, wordBreak: "break-all", "&:hover": { textDecoration: "underline" } }}
                    >
                      {viewSubdomain.name}
                      <OpenInNewIcon sx={{ fontSize: 12, flexShrink: 0 }} />
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Status badges row (below the grid, like the Server modal) */}
              <Box sx={{ mt: 2.5, display: "flex", gap: 1.25, flexWrap: "wrap" }}>
                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, px: 1.5, py: 0.6, borderRadius: "999px", fontWeight: 700, fontSize: 11, color: viewSubdomain.status === "Active" ? "#16A34A" : colors.textMuted, background: viewSubdomain.status === "Active" ? "rgba(22,163,74,0.12)" : "rgba(148,163,184,0.12)" }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: viewSubdomain.status === "Active" ? "#16A34A" : colors.textMuted, flexShrink: 0 }} />
                  {viewSubdomain.status || "Unknown"}
                </Box>
                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", px: 1.5, py: 0.6, borderRadius: "999px", fontWeight: 700, fontSize: 11, color: "#3B82F6", background: "rgba(59, 130, 246, 0.12)" }}>
                  Sub-Domain
                </Box>
                {viewSubdomain.categoryName && (
                  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", px: 1.5, py: 0.6, borderRadius: "999px", fontWeight: 700, fontSize: 11, color: colors.text, background: colors.hover }}>
                    {viewSubdomain.categoryName}
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${colors.divider}` }}>
              <Button onClick={() => setViewSubdomain(null)}>Close</Button>
              {canEdit && (
                <Button
                  variant="contained"
                  onClick={() => {
                    setViewSubdomain(null);
                    setEditSub(viewSubdomain);
                  }}
                  sx={{ background: "#3B82F6", "&:hover": { background: "#2563EB" } }}
                >
                  Edit sub-domain
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

    </DashboardLayout>
  );
}

export default DomainList;
